import React, { useContext, useState } from 'react';
import type { ICellRendererParams } from 'ag-grid-community';
import type { Fund, Portfolio } from 'venn-api';
import { Icon } from 'venn-ui-kit';
import type { RowData } from '../../../../modals/data-uploader/types';
import { MultiPortfolioReviewContext } from '../../../../modals/data-uploader/views/components/common/MultiPortfolioReviewContext';
import { StrategyAssignmentPopover } from '../../../../modals/data-uploader/views/components/StrategyAssignmentPopover';
import { useHasFF } from 'venn-utils';
import { serializePath } from '../../../../modals/data-uploader/views/review/helpers';

interface StrategyAssignmentCellRendererProps {
  showAssignButton: boolean;
  fund: Fund | undefined;
  path: number[] | undefined;
}

export const StrategyAssignmentCellRenderer = ({
  showAssignButton,
  fund,
  path,
}: ICellRendererParams<RowData<Portfolio>> & StrategyAssignmentCellRendererProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const context = useContext(MultiPortfolioReviewContext);
  const hasManualStrategyAssignment = useHasFF('rcm_strategy_enforcement_ff');

  if (!context) return null;

  const {
    data: { selectedIndex, parsedResults, strategyData },
    actions: { assignInvestment },
  } = context;

  if (!showAssignButton || !fund || !path) return null;

  const isExcluded = parsedResults?.[selectedIndex]?.excludedInvestments?.has(serializePath(path).value);
  const isManuallyAssigned = fund?.id ? strategyData.manualAssignments?.has(fund.id) : false;
  const isAssigned = Array.isArray(path) && path.length > 2;

  if ((isAssigned && !isManuallyAssigned) || isExcluded) return null;

  const handleStrategySelect = (strategyId: string, _strategyName: string) => {
    if (selectedIndex !== undefined) {
      try {
        assignInvestment(selectedIndex, fund, strategyId, true); // Manual assignment via UI
        setIsOpen(false);
      } catch (_error) {
        setIsOpen(false);
      }
    }
  };

  return (
    <StrategyAssignmentPopover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      onStrategySelect={handleStrategySelect}
      investmentName={fund.name || 'Investment'}
      disabled={!hasManualStrategyAssignment}
    >
      <button
        type="button"
        className={`
          flex self-center justify-center items-center text-center gap-[6px] 
          text-[12px] rounded-[4px] h-[24px]
          ${
            isManuallyAssigned
              ? 'min-w-[88px] bg-transparent border border-solid border-venn-grey-80 text-venn-grey-80 hover:text-venn-grey-80'
              : 'min-w-[73px] bg-venn-error border border-solid border-venn-error text-white hover:text-white'
          }
        `}
      >
        {isManuallyAssigned ? (
          <>
            Reassign
            <Icon type="magnifying-glass" />
          </>
        ) : (
          <>
            Assign
            <Icon type="magnifying-glass" />
          </>
        )}
      </button>
    </StrategyAssignmentPopover>
  );
};
